import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Loader, SdirCheckbox, Textarea } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { useCurrentApplication } from '../../../services/hooks';
import { overruleApi } from '../../../httpclient';

/**
 * Overprøvingen slik kravsiden trenger den: avkryssingen, kommentaren, og
 * lagringen som kjøres når søkeren trykker «Neste».
 *
 * Tilstanden ligger i en hook fordi de to delene sitter hver sin plass —
 * seksjonen tegnes midt på siden, mens lagringen henger på «Neste»-knappen i
 * sidefoten — og de må dele nøyaktig samme avkryssing.
 */
export interface CertReqOverrule {
  /** Har søkeren bedt om at en saksbehandler ser på søknaden likevel? */
  checked: boolean;
  comment: string;
  /** Er kommentaren etterspurt, men fortsatt tom? */
  showValidation: boolean;
  /** Feilen fra lagringen, om den feilet. */
  error?: Error | string;
  saving: boolean;
  setChecked: (checked: boolean) => void;
  setComment: (comment: string) => void;
  /**
   * Lagrer — eller rydder bort — overprøvingen.
   *
   * Svaret er om søkeren kan gå videre: `false` betyr at hun skal bli stående,
   * enten fordi kommentaren mangler eller fordi lagringen feilet.
   */
  save: () => Promise<boolean>;
}

/**
 * Overprøvingens mekanikk — identisk med den gamle kvalifikasjonssidens.
 *
 * Avkryssingen lagres som en kommentar på søknaden gjennom
 * `overruleApi.v1OverruleAttachCommandPut`, som setter `userComment`. Søknaden
 * er fasit: står det en kommentar der fra før, står haken i når siden åpnes, og
 * fjernes haken tømmes feltet igjen.
 *
 * Kommentaren er **påkrevd** når haken står i, som i legacy: en overprøving
 * uten en begrunnelse gir saksbehandleren ingenting å vurdere.
 */
export const useCertReqOverrule = (): CertReqOverrule => {
  const { application } = useCurrentApplication();
  const [checked, setChecked] = useState(false);
  const [comment, setComment] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  const {
    loading: saving,
    errorObject: error,
    callApi: saveOverrule,
  } = useApi({
    apiFunction: (payload) => overruleApi.v1OverruleAttachCommandPut(payload),
    callApiOnInit: false,
  });

  const applicationUid = application.result?.uid;
  const userComment = application.result?.userComment;

  useEffect(() => {
    setChecked(!!userComment);
    setComment(userComment || '');
  }, [userComment]);

  const handleChecked = useCallback((next: boolean) => {
    setChecked(next);
    // Haken tas bort: kravet om en kommentar gjelder ikke lenger.
    if (!next) setShowValidation(false);
  }, []);

  const handleComment = useCallback((value: string) => {
    setComment(value);
    if (value) setShowValidation(false);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!applicationUid) return false;

    try {
      if (checked) {
        if (!comment) {
          setShowValidation(true);
          return false;
        }
        setShowValidation(false);
        const res = await saveOverrule({ applicationUid, comment });
        return !!res?.success;
      }

      // Haken er borte. Er det lagret en overprøving fra før, ryddes den bort —
      // ellers ville søknaden fortsatt bedt om manuell vurdering.
      if (userComment) {
        const res = await saveOverrule({ applicationUid, comment: '' });
        return !!res?.success;
      }

      return true;
    } catch {
      return false;
    }
  }, [applicationUid, checked, comment, saveOverrule, userComment]);

  // Objektet memoiseres slik at «Neste»-knappens callback holder seg i ro
  // mellom tegninger der ingenting av overprøvingen har endret seg.
  return useMemo(
    () => ({
      checked,
      comment,
      showValidation,
      error,
      saving,
      setChecked: handleChecked,
      setComment: handleComment,
      save,
    }),
    [checked, comment, showValidation, error, saving, handleChecked, handleComment, save]
  );
};

interface RequirementOverruleProps {
  overrule: CertReqOverrule;
}

/**
 * «Har du nødvendig kompetanse som oppfyller kravene for sertifikatet, men
 * systemet er uenig med deg?»
 *
 * Seksjonen står nederst på kravsiden, etter kravene, og bare når det faktisk
 * er noe å være uenig om — se `showOverrule` i `CertReqRequirementsPage`.
 * Teksten og lokaliseringsnøklene er den gamle kvalifikasjonssidens, ordrett:
 * søkeren skal møte den samme setningen uansett hvilken av de to sidene
 * sertifikatet hennes havner på.
 *
 * Filopplastingen fra den gamle siden er ikke med. Der var den eneste måten å
 * legge ved noe på en søknad systemet hadde avvist; her har hvert eneste krav
 * sin egen opplastingsknapp.
 */
export const RequirementOverrule: React.FC<RequirementOverruleProps> = ({ overrule }) => {
  const intl = useIntl();

  return (
    <Section data-testid="certreq-overrule">
      <Heading>{intl.formatMessage({ id: 'qualification.overrule.title' })}</Heading>

      <SdirCheckbox
        checked={overrule.checked}
        title={intl.formatMessage({ id: 'qualification.overrule.toggle' })}
        onClick={() => overrule.setChecked(!overrule.checked)}
      />

      {overrule.checked && (
        <Comment>
          <Textarea
            placeholder={intl.formatMessage({ id: 'qualification.overrule.comment' })}
            contentValue={overrule.comment}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              overrule.setComment(e?.target?.value || '')
            }
            error={
              overrule.showValidation ? intl.formatMessage({ id: 'general.form.error' }) : undefined
            }
          />
        </Comment>
      )}

      {overrule.saving && <Loader />}
      {overrule.error ? <ErrorMessage error={overrule.error} /> : null}
    </Section>
  );
};

/**
 * Seksjonen står tydelig fra kravene over: den er ikke et krav, men et spørsmål
 * til søkeren. Luften over er derfor større enn mellomrommet mellom kravene.
 */
const Section = styled.section`
  margin-top: 2.5rem;
`;

const Heading = styled.h3`
  margin: 0 0 1rem;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.35;
`;

const Comment = styled.div`
  max-width: 600px;
  margin: 1.25rem 0 0;
`;

export default RequirementOverrule;
